import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { TimeSignal } from 'graphql/types';
import { useMeContext } from 'modules/me';
import { getLuxonJsDateTimeFormatFromMe } from 'utils/date-time';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    paddingLeft: theme.spacing(1),
    whiteSpace: 'nowrap'
  },
  modified: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center'
  }
}));

export type NoteHeaderProps = {
  title: string;
  timeSignal?: TimeSignal;
};

export const NoteHeader: React.FC<NoteHeaderProps> = ({
  title,
  timeSignal
}: NoteHeaderProps) => {
  const classes = useStyles();
  const me = useMeContext();
  const hasEdited = useMemo(() => {
    if (timeSignal?.creationTime && timeSignal?.modificationTime) {
      return (
        DateTime.fromISO(timeSignal!.modificationTime) >
        DateTime.fromISO(timeSignal!.creationTime)
      );
    }
    return false;
  }, [timeSignal]);

  const lastEditedValue = useMemo(() => {
    const format = getLuxonJsDateTimeFormatFromMe(me);

    return {
      datetime: timeSignal?.modificationTime
        ? DateTime.fromISO(timeSignal!.modificationTime).toFormat(format)
        : undefined
    };
  }, [me, timeSignal]);

  return (
    <div className={classes.container}>
      <div className={classes.titleContainer}>
        <Typography
          variant="h4"
          className={classes.title}
          data-testid="add-note-title-text"
        >
          {title}
        </Typography>
      </div>
      {timeSignal?.modificationTime && (
        <Typography data-testid="last-edited-text" variant="caption">
          <FormattedMessage
            id={hasEdited ? 'note.lastEdit' : 'note.createdAt'}
            values={lastEditedValue}
          />
        </Typography>
      )}
    </div>
  );
};
